import { useMemo } from "react"

import NumberFlow from "@number-flow/react"
import { Card, Spin, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import { useAnimatedNumber } from "../../../../hooks/useAnimatedNumber"
import { lightTheme } from "../../../../theme"
import TrendLineChart from "../../charts/TrendLineChart"
import { useCardData } from "../../hooks/useCardData"

const { Text, Title } = Typography

const CHART_HEIGHT = 300
const NO_DECIMALS = { maximumFractionDigits: 0 }

// Lookups overview (DAA-3-01 … DAA-3-04), the Alerts Dashboard's Alerts overview in an outlined
// card as the design draws it: the heading, the period's lookup total and "<N> <comparison label>"
// above a solid "Current period" line with its area and a dashed "Previous period" one, and the
// platform's comparison tooltip: each period's date and Total side by side (CHA1-4525). No info
// icon, legend or corner tag.
const LookupsOverviewCard = () => {
  const { t } = useTranslation()
  const isMobile = useSelector((state) => state.nav.isMobile)
  const { data, loading } = useCardData("lookupsOverview")
  const total = useAnimatedNumber(data?.total)
  const comparisonTotal = useAnimatedNumber(data?.comparison?.total)
  const series = data?.series

  const lines = useMemo(
    () => [
      { label: t("Current period"), color: lightTheme.token.colorPrimary, data: series?.current }
    ],
    [series, t]
  )
  const comparisonLines = useMemo(
    () =>
      series?.comparison
        ? [
            {
              label: t("Previous period"),
              data: series.comparison,
              dates: series.comparison_labels
            }
          ]
        : null,
    [series, t]
  )

  return (
    <Card data-intro className="card-chart-wrapper">
      <Spin spinning={loading}>
        <div>
          <Text strong style={{ fontSize: 14 }}>{`${t("Lookups overview")} `}</Text>
          <Text type="secondary" strong style={{ fontSize: 14 }}>
            {t("by count")}
          </Text>
        </div>
        <Title
          level={1}
          style={{
            textAlign: "left",
            margin: "4px 0 0",
            fontWeight: 400,
            fontSize: 24,
            lineHeight: 1.2
          }}
        >
          {/* "-" until the first answer, so a failed first request does not read as 0 lookups. */}
          {data ? <NumberFlow value={total} locales="en-US" format={NO_DECIMALS} /> : "-"}
        </Title>
        {data?.comparison && (
          <Text type="secondary" style={{ fontSize: isMobile ? 12 : 14 }}>
            <NumberFlow value={comparisonTotal} locales="en-US" format={NO_DECIMALS} />{" "}
            {t(data.comparison.label)}
          </Text>
        )}
        {/* The chart's place is kept until the first answer, so the page does not jump under it. */}
        <div style={{ height: CHART_HEIGHT, marginTop: 24 }}>
          {series && (
            <TrendLineChart
              labels={series.labels}
              series={lines}
              comparison={comparisonLines}
              lastIntervalIncomplete={series.last_interval_incomplete}
              fill
              legend={false}
              height={CHART_HEIGHT}
              tooltipColumns
            />
          )}
        </div>
      </Spin>
    </Card>
  )
}

export default LookupsOverviewCard
